/**
 * Smart-sticky behavior for the product info column.
 *
 * Why: when the info column is taller than the viewport, plain
 * `position: sticky; top: 0` pins its top while the user scrolls down,
 * hiding the bottom of the info area until the image column ends.
 *
 * How: dynamically update the sticky `top` so the column scrolls with
 * the page until one of its edges meets the viewport edge — sticks to
 * viewport bottom on scroll-down, sticks to viewport top on scroll-up.
 */
(function () {
  var MIN_WIDTH = 750;
  var SELECTOR = '.product__column-sticky';
  var INIT_FLAG = 'smartStickyInitialized';
  var BOTTOM_GAP = 30;

  function SmartSticky(el) {
    this.el = el;
    this.rafPending = false;
    this.lastScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    this.baseTop = this.readBaseTop();
    this.topValue = this.baseTop;

    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.scheduleUpdate = this.scheduleUpdate.bind(this);

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize, { passive: true });

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.scheduleUpdate);
      this.resizeObserver.observe(el);
    }

    var elH = el.offsetHeight;
    var vH = window.innerHeight;
    var minTop = Math.min(0, vH - elH - BOTTOM_GAP);
    this.topValue = Math.max(minTop, this.baseTop - this.lastScrollY);
    this.applyTop();
  }

  SmartSticky.prototype.readBaseTop = function () {
    var inline = this.el.style.top;
    this.el.style.top = '';
    var computed = parseFloat(window.getComputedStyle(this.el).top);
    this.el.style.top = inline;
    return isFinite(computed) ? computed : 0;
  };

  SmartSticky.prototype.isActive = function () {
    return window.matchMedia('(min-width: ' + MIN_WIDTH + 'px)').matches;
  };

  SmartSticky.prototype.onScroll = function () {
    this.scheduleUpdate();
  };

  SmartSticky.prototype.onResize = function () {
    if (!this.isActive()) {
      this.el.style.top = '';
      return;
    }
    this.baseTop = this.readBaseTop();
    this.scheduleUpdate();
  };

  SmartSticky.prototype.scheduleUpdate = function () {
    if (this.rafPending) return;
    this.rafPending = true;
    var self = this;
    requestAnimationFrame(function () {
      self.rafPending = false;
      self.update();
    });
  };

  SmartSticky.prototype.applyTop = function () {
    this.el.style.top = this.topValue + 'px';
  };

  SmartSticky.prototype.update = function () {
    if (!this.isActive()) {
      this.el.style.top = '';
      return;
    }

    var scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    var delta = scrollY - this.lastScrollY;
    this.lastScrollY = scrollY;

    var elH = this.el.offsetHeight;
    var vH = window.innerHeight;
    var headerOffset = this.baseTop;

    if (elH <= vH - headerOffset) {
      this.topValue = headerOffset;
      this.applyTop();
      return;
    }

    var minTop = vH - elH - BOTTOM_GAP;
    var maxTop = headerOffset;
    var next = this.topValue - delta;
    if (next < minTop) next = minTop;
    if (next > maxTop) next = maxTop;
    this.topValue = next;
    this.applyTop();
  };

  function init() {
    var els = document.querySelectorAll(SELECTOR);
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.dataset[INIT_FLAG]) continue;
      el.dataset[INIT_FLAG] = 'true';
      new SmartSticky(el);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
